import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {

  const [open, setOpen] = useState(false);
  const [hero, setHero] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const { t, i18n } = useTranslation();

  const navLinks = [
    { label: t("navbar_home"), href: "#home" },
    { label: t("navbar_services"), href: "#services" },
    { label: t("navbar_transaction"), href: "#transaction" },
    { label: t("navbar_partner"), href: "#partners" },
    { label: t("navbar_contact"), href: "#contact" },
  ];

  useEffect(() => {
    const fetchHero = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/hero`);
        setHero(res.data);
      } catch (err) {
        console.log("Failed to fetch hero data:", err);
      }
    };

    fetchHero();
  }, []);

  useEffect(() => {

    const handleNavbarScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleNavbarScroll);

    return () => {
      window.removeEventListener("scroll", handleNavbarScroll);
    };

  }, []);

  // Smooth Scroll
  const handleScroll = (e, href) => {

    e.preventDefault();

    const target = document.querySelector(href);

    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    setOpen(false);
  };

  // Language Toggle
  const changeLanguage = () => {

    const newLang = i18n.language === "bn" ? "en" : "bn";

    i18n.changeLanguage(newLang);

    localStorage.setItem("language", newLang);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b backdrop-blur-lg
      ${
        scrolled
          ? "bg-white/80 border-gray-200 shadow-sm"
          : "bg-white border-gray-200 shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">

        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => handleScroll(e, "#home")}
          className="flex items-center gap-2 text-xl font-bold text-primary"
        >
          {hero?.logo ? (
            <img
              src={
                hero.logo.startsWith("http")
                  ? hero.logo
                  : `${BASE_URL}${hero.logo}`
              }
              alt="Logo"
              className="h-8 object-contain"
            />
          ) : (
            "SENDORA"
          )}
        </a>

        {/* Desktop Menu */}
        <ul className="hidden md:flex items-center gap-8">

          {navLinks.map((link) => (
            <li key={link.label}>
              <a
                href={link.href}
                onClick={(e) => handleScroll(e, link.href)}
                className="text-sm font-medium text-gray-600 hover:text-primary transition-colors"
              >
                {link.label}
              </a>
            </li>
          ))}

        </ul>

        {/* Desktop Buttons */}
        <div className="hidden md:flex items-center gap-4">

          {/* Translate Button */}
          <button
            onClick={changeLanguage}
            className="relative flex items-center w-[78px] h-10 rounded-full bg-primary/10 border border-primary/20 p-1 transition-all duration-300"
            >
            
            {/* Sliding Circle */}
            <div
              className={`absolute top-1 w-8 h-8 rounded-full bg-primary shadow-md transition-all duration-300
              ${
                i18n.language === "bn"
                  ? "left-1"
                  : "left-[38px]"
              }`}
            />

            {/* BN */}
            <div className="flex-1 flex items-center justify-center z-10">
              <span
                className={`text-xs font-bold transition-colors duration-300 ${
                  i18n.language === "bn"
                    ? "text-white"
                    : "text-primary"
                }`}
              >
                বাং
              </span>
            </div>

            {/* EN */}
            <div className="flex-1 flex items-center justify-center z-10">
              <span
                className={`text-xs font-bold transition-colors duration-300 ${
                  i18n.language === "en"
                    ? "text-white"
                    : "text-primary"
                }`}
              >
                EN
              </span>
            </div>

          </button>

          {/* Login Button */}
          <Link
            to="/login"
            className="inline-flex items-center px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            {t("navbar_login")}
          </Link>

        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-gray-700 text-2xl"
          onClick={() => setOpen(!open)}
        >
          {open ? "✖" : "☰"}
        </button>

      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out ${
          open ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-white border-b border-gray-200 px-6 pb-6">

          <ul className="flex flex-col gap-4 pt-4">

            {navLinks.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  onClick={(e) => handleScroll(e, link.href)}
                  className="text-sm font-medium text-gray-600 hover:text-primary transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}

            {/* Mobile Translate Button */}
            <li>
              <button
                onClick={changeLanguage}
                className="relative flex items-center w-[78px] h-10 rounded-full bg-primary/10 border border-primary/20 p-1 transition-all duration-300"
                >
                
                {/* Sliding Circle */}
                <div
                  className={`absolute top-1 w-8 h-8 rounded-full bg-primary shadow-md transition-all duration-300
                  ${
                    i18n.language === "bn"
                      ? "left-1"
                      : "left-[38px]"
                  }`}
                />

                {/* BN */}
                <div className="flex-1 flex items-center justify-center z-10">
                  <span
                    className={`text-xs font-bold transition-colors duration-300 ${
                      i18n.language === "bn"
                        ? "text-white"
                        : "text-primary"
                    }`}
                  >
                    বাং
                  </span>
                </div>

                {/* EN */}
                <div className="flex-1 flex items-center justify-center z-10">
                  <span
                    className={`text-xs font-bold transition-colors duration-300 ${
                      i18n.language === "en"
                        ? "text-white"
                        : "text-primary"
                    }`}
                  >
                    EN
                  </span>
                </div>

              </button>
            </li>

            {/* Mobile Login */}
            <li>
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="inline-flex items-center px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                {t("navbar_login")}
              </Link>
            </li>

          </ul>

        </div>
      </div>
    </nav>
  );
};

export default Navbar;